import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import InputWithVariablePicker from './InputWithVariablePicker';

const SubtaskTemplateModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  subtask = null,
  teamMembers = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'Normal',
    default_stage: 'To Do',
    estimated_effort_value: '',
    estimated_effort_unit: 'hours',
    due_date_offset: '',
    start_date_offset: '',
    assignees: [],
    tags: [],
    checklist_items: [],
    category: 'General'
  });

  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');

  useEffect(() => {
    if (subtask) {
      setFormData({
        name: subtask.name || '',
        description: subtask.description || '',
        priority: subtask.priority || 'Normal',
        default_stage: subtask.default_stage || 'To Do',
        estimated_effort_value: subtask.estimated_effort_value || '',
        estimated_effort_unit: subtask.estimated_effort_unit || 'hours',
        due_date_offset: subtask.due_date_offset || '',
        start_date_offset: subtask.start_date_offset || '',
        assignees: subtask.assignees || [],
        tags: subtask.tags || [],
        checklist_items: subtask.checklist_items || [],
        category: subtask.category || 'General'
      });
    } else {
      // Reset form for new subtask
      setFormData({
        name: '',
        description: '',
        priority: 'Normal',
        default_stage: 'To Do',
        estimated_effort_value: '',
        estimated_effort_unit: 'hours',
        due_date_offset: '',
        start_date_offset: '',
        assignees: [],
        tags: [],
        checklist_items: [],
        category: 'General'
      });
    }
  }, [subtask, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Subtask name is required');
      return;
    }
    onSave(formData);
    onClose();
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleAddChecklistItem = (e) => {
    e.preventDefault();
    if (newChecklistItem.trim()) {
      setFormData({
        ...formData,
        checklist_items: [...formData.checklist_items, { text: newChecklistItem.trim(), completed: false }]
      });
      setNewChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (index) => {
    setFormData({
      ...formData,
      checklist_items: formData.checklist_items.filter((_, i) => i !== index)
    });
  };

  const toggleAssignee = (userId) => {
    const assignees = formData.assignees.includes(userId)
      ? formData.assignees.filter(id => id !== userId)
      : [...formData.assignees, userId];
    setFormData({ ...formData, assignees });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {subtask ? 'Edit Subtask Template' : 'Add Subtask Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtask Name *
              </label>
              <InputWithVariablePicker
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Research Requirements, Setup Meeting"
                required
                showVariablePicker={true}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <InputWithVariablePicker
                type="textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this subtask involves..."
                rows={4}
                showVariablePicker={true}
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="General, Design, Development, etc."
              />
            </div>

            {/* Priority & Stage */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="Low">Low</option>
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Stage
                </label>
                <select
                  value={formData.default_stage}
                  onChange={(e) => setFormData({ ...formData, default_stage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="To Do">To Do</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Review">Review</option>
                  <option value="Done">Done</option>
                </select>
              </div>
            </div>

            {/* Estimated Effort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Effort
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  min="0"
                  value={formData.estimated_effort_value}
                  onChange={(e) => setFormData({ ...formData, estimated_effort_value: e.target.value ? parseInt(e.target.value) : '' })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., 2, 4, 8"
                />
                <select
                  value={formData.estimated_effort_unit}
                  onChange={(e) => setFormData({ ...formData, estimated_effort_unit: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>

            {/* Date Offsets */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date Offset
                </label>
                <input
                  type="text"
                  value={formData.start_date_offset}
                  onChange={(e) => setFormData({ ...formData, start_date_offset: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., +1 day, +2 weeks"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date Offset
                </label>
                <input
                  type="text"
                  value={formData.due_date_offset}
                  onChange={(e) => setFormData({ ...formData, due_date_offset: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., +5 days, +1 week"
                />
              </div>
            </div>

            {/* Assignees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Assignees
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                {!teamMembers || !Array.isArray(teamMembers) || teamMembers.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No team members available</p>
                ) : (
                  <div className="space-y-2">
                    {teamMembers.map((member) => (
                      <label key={member.user?.id || member.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.assignees.includes(member.user?.id || member.id)}
                          onChange={() => toggleAssignee(member.user?.id || member.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{member.user?.name || member.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag(e)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Add a tag..."
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Checklist Items */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Checklist Items
              </label>
              <div className="space-y-2 mb-3">
                {formData.checklist_items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-gray-50 p-2 rounded">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveChecklistItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem(e)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Add checklist item..."
                />
                <button
                  type="button"
                  onClick={handleAddChecklistItem}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {subtask ? 'Update Subtask' : 'Add Subtask'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubtaskTemplateModal;
